import type { PatternDefinition, PatternLayer, PatternMark } from './types'

export function definePattern(...layers: PatternLayer[]): PatternDefinition {
  for (const layer of layers) {
    if (layer.tile && (!Number.isFinite(layer.tile.width) || !Number.isFinite(layer.tile.height) || layer.tile.width <= 0 || layer.tile.height <= 0)) {
      throw new Error('Pattern tile dimensions must be finite and positive')
    }
  }
  return { layers }
}

/** Later definitions paint above earlier definitions; their tile sizes stay independent. */
export function compose(...patterns: PatternDefinition[]): PatternDefinition {
  return {
    layers: patterns.flatMap(pattern => pattern.layers),
    frame: patterns.some(pattern => pattern.frame)
      ? seconds => patterns.flatMap(pattern => pattern.frame?.(seconds) ?? pattern.layers)
      : undefined,
  }
}

export function animatePattern(
  pattern: PatternDefinition,
  frame: (seconds: number) => readonly PatternLayer[],
): PatternDefinition {
  return { ...pattern, frame }
}

export function cutout(pattern: PatternDefinition, marks: readonly PatternMark[]): PatternDefinition {
  const apply = (layers: readonly PatternLayer[]) => layers.map(layer => ({ ...layer, cutouts: [...(layer.cutouts ?? []), ...marks] }))
  const frame = pattern.frame
  return { layers: apply(pattern.layers), frame: frame ? seconds => apply(frame(seconds)) : undefined }
}

export function resolvePaint(paint: string | undefined, colors: Readonly<Record<string, string>>): string | undefined {
  return paint?.startsWith('@') ? colors[paint.slice(1)] ?? 'currentColor' : paint
}

export function positive(value: number, name: string): number {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${name} must be finite and positive`)
  return value
}

/** Indexed hash: reproducible across platforms, renders, and recipe instances. */
export function randomAt(seed: number, index: number): number {
  let value = Math.imul(Math.trunc(seed) ^ (index + 1), 0x45d9f3b)
  value = Math.imul(value ^ (value >>> 16), 0x45d9f3b)
  return ((value ^ (value >>> 16)) >>> 0) / 0xffffffff
}

export const emptyPatternColors: Readonly<Record<string, string>> = {}

/**
 * React keys for a layer's marks. Seeded recipes can emit identical marks (same kind, position and paint); the
 * occurrence count keeps those keys unique without depending on array position.
 */
export function keyedMarks<Mark extends PatternMark>(marks: readonly Mark[]): Array<{ key: string; mark: Mark }> {
  const seen = new Map<string, number>()
  return marks.map(mark => {
    const base = markKey(mark)
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    return { key: count === 0 ? base : `${base}#${count}`, mark }
  })
}

export function markKey(mark: PatternMark): string {
  if (mark.id) return mark.id
  switch (mark.kind) {
    case 'rect': return `rect:${mark.x}:${mark.y}:${mark.width}:${mark.height}:${mark.fill}`
    case 'circle': return `circle:${mark.cx}:${mark.cy}:${mark.fill}`
    case 'path': return `path:${mark.d}:${mark.stroke}:${mark.strokeWidth}`
    case 'image': return `image:${mark.href}:${mark.x}:${mark.y}`
  }
  throw new Error('Unknown pattern mark')
}
